import { getDefaultMission, type MissionLength } from './domainMissions';

/**
 * Domain readiness — Act I build item 4.
 *
 * ⭐⭐ **Domains are gated by READINESS, not by act.** Act I recommends three,
 * requires one and forbids none, so "which domains may this user operate
 * through?" cannot be answered by a chapter number. It is answered here, by what
 * the user has actually done with each domain.
 *
 * ⭐ Only ONE state is stored: `discovered` (`userStore.domainsDiscovered`).
 * Meeting a domain is the single step that leaves no other trace. Everything
 * above it derives from artifacts that already exist, which is what keeps the
 * ladder honest — and what makes remapping chapters free.
 */

/** The ladder, low to high. A domain sits at the HIGHEST rung it has reached. */
export type DomainReadiness =
  | 'unknown'     // never encountered
  | 'discovered'  // understands what it represents (ch4's introduction)
  | 'mapped'      // knows where they stand (the Life Map)
  | 'activated'   // knows where it is going (a Quest)
  | 'developed';  // is improving (a milestone actually reached)

/** Low to high. Exported so a UI can compare rungs without re-listing them. */
export const READINESS_LADDER: DomainReadiness[] = [
  'unknown', 'discovered', 'mapped', 'activated', 'developed',
];

export const readinessRank = (r: DomainReadiness): number => READINESS_LADDER.indexOf(r);

/** Everything readiness needs from a Quest, and nothing more. */
export interface ReadinessQuest {
  /** The domain id — `Quest.category`. */
  category: string;
  status: 'active' | 'completed' | 'archived';
  goals: { completed: boolean }[];
}

export interface DomainReadinessSignals {
  /**
   * `domainId → ISO date first met`. Straight from `userStore.domainsDiscovered`.
   */
  discovered: Record<string, string | undefined>;
  /**
   * Whether a Life Map sitting has been COMPLETED.
   *
   * ⭐ One flag, not a per-domain set, because the flow maps all seven in a
   * single sitting — there is no such thing as a half Life Map to record.
   * ⚠️ Derive this from `attemptHistory` (persisted, fetched everywhere), NOT
   * from `latestResults`, which is an in-memory cache only the Assessment and
   * Profile tabs populate. A readiness surface elsewhere would read it as empty
   * and quietly demote every domain.
   */
  hasLifeMap: boolean;
  /** Every quest the user owns. Filtered per domain here. */
  quests: ReadinessQuest[];
  /**
   * `domainId → ISO date the user declared it`. Straight from
   * `userStore.domainsDeveloping`.
   *
   * ⚠️ The ONLY user-declared rung, and the only one this module cannot check.
   * See `hasDeclaredDeveloping` for why that is deliberate rather than a gap.
   */
  declaredDeveloping: Record<string, string | undefined>;
}

/**
 * ⚠️⚠️ **Activated derives from the QUEST, never from the Domain Mission.**
 *
 * `constants/domainMissions.ts` ships prewritten missions for all seven domains,
 * and `userStore.missions` is a plain `Record<string, string>` that does not
 * preserve the authored-vs-seeded distinction the legacy `useDomainMissionsStore`
 * tracked as `isCustomMission`. Derive activation from the mission and every
 * domain reads Activated for anyone who has been through the Foundation flow —
 * a rule that is always true, which is the same as no rule at all.
 *
 * Nothing seeds a per-domain quest, so this cannot be faked by a default. The
 * chapter still ASKS for a Mission alongside the Quest; the derivation just
 * cannot use it as evidence.
 */
const isActivated = (quests: ReadinessQuest[]) =>
  quests.some(q => q.status !== 'archived');

/**
 * ⭐⭐ **Developing is SUBJECTIVE — the user says so, and nothing measures them.**
 * Owner call 2026-07-30.
 *
 * Every other rung is a fact the app can check: a marker exists, a Life Map
 * exists, a Quest exists. "Am I actually developing here?" is not that kind of
 * question — it was previously derived from a completed Quest or Goal, which
 * meant the app awarding a permanent badge for one milestone possibly finished
 * months ago, and withholding it from someone genuinely working hard in a domain
 * whose first milestone is still ahead of them. Both readings were wrong in a way
 * no threshold fixes, because the honest answer lives with the person.
 *
 * ⏳ An OBJECTIVE version (recent measurable movement, e.g. a milestone inside a
 * 30-day window) is wanted later — that is why the declaration is stored with the
 * DATE it was made rather than as a bare boolean: a future rule can ask how long
 * ago someone said this and whether the evidence has kept up.
 *
 * ⚠️ It still requires an active Quest. The rung sits above Activated, and
 * "developing" presupposes something to develop toward; a domain with no
 * direction at all reading Developing would make the ladder mean nothing. Marking
 * is therefore only OFFERED once a domain is activated.
 */
const hasDeclaredDeveloping = (domainId: string, s: DomainReadinessSignals) =>
  Boolean(s.declaredDeveloping[domainId]);

/**
 * Where one domain stands.
 *
 * ⚠️ The rungs are checked HIGHEST FIRST and each is independent — this returns
 * the highest rung reached, it does not require the ones below it. A user who
 * writes a Quest for Social in week one without ever opening the Life Map is
 * Activated in Social, and saying otherwise would be the model arguing with the
 * facts. Reorder these checks and a developed domain reports as merely mapped.
 */
export function deriveDomainReadiness(
  domainId: string,
  s: DomainReadinessSignals,
): DomainReadiness {
  const quests = s.quests.filter(q => q.category === domainId);

  if (isActivated(quests) && hasDeclaredDeveloping(domainId, s)) return 'developed';
  if (isActivated(quests)) return 'activated';
  if (s.hasLifeMap) return 'mapped';
  if (s.discovered[domainId]) return 'discovered';
  return 'unknown';
}

export function deriveAllDomainReadiness(
  domainIds: string[],
  s: DomainReadinessSignals,
): Record<string, DomainReadiness> {
  return Object.fromEntries(domainIds.map(id => [id, deriveDomainReadiness(id, s)]));
}

/* ══════════════════════════════════════════════════════════════════════════════
   Mapped AND missioned — the shared predicate (L4 of the Life Map merge)
   ══════════════════════════════════════════════════════════════════════════════ */

/**
 * Whether this domain's mission was actually WRITTEN by the user.
 *
 * ⚠️⚠️ **A MISSION BYTE-IDENTICAL TO ITS SHIPPED DEFAULT DOES NOT COUNT** (owner,
 * 2026-08-09). `purposeVisionService.craftMission` falls back to
 * `getDefaultMission(domainId, 'brief')` whenever the AI is unavailable, and that
 * text lands in the editor and saves like any other — so *Craft with AI → Save*
 * with the proxy down would otherwise satisfy every gate below without the user
 * typing a word. Both default forms are refused, not just the one the fallback
 * returns, because the legacy `domain_stats` editor defaulted to `detailed`.
 *
 * ⚠️ This is a CONTENT check, deliberately, not an authorship one. Nothing seeds
 * `userStore.missions` (verified 2026-08-09: `DEFAULT_DOMAIN_MISSIONS` is read
 * only by the legacy store and by that AI fallback), so presence alone is nearly
 * sound — but "nearly" is doing real work here, and the text is the thing the
 * user is claiming to have written.
 */
export function hasAuthoredMission(
  missions: Record<string, string | undefined>,
  domainId: string,
): boolean {
  return missionState(missions, domainId) === 'authored';
}

const MISSION_LENGTHS: MissionLength[] = ['brief', 'detailed'];

/**
 * ⭐⭐ **THE THREE STATES AN OBJECTIVE CAN BE IN**, named — because two of them
 * used to be indistinguishable to everything downstream and one of them cost a
 * tester four domains.
 *
 * ⚠️⚠️ `'untouched'` IS THE ONE THAT MATTERS. A domain whose objective is
 * byte-identical to its shipped default LOOKS finished on screen and is refused
 * everywhere else, and until now nothing anywhere could say which of the two
 * absences it was. Ali (tester, 2026-09-10) had five objectives written and saw
 * four domains on Build: Material's text was `domainMissions.ts`'s `brief`, word
 * for word, and no surface in the app mentioned it.
 * ⭐ Naming the state is what lets the Objective panel and the Build page each
 * say the true sentence instead of a vague one.
 */
export type MissionState = 'missing' | 'untouched' | 'authored';

export function missionState(
  missions: Record<string, string | undefined>,
  domainId: string,
): MissionState {
  const text = missions[domainId]?.trim();
  if (!text) return 'missing';
  // ⚠️ Both default forms, not just the one the AI fallback wrote: the legacy
  // `domain_stats` editor defaulted to `detailed`.
  // ⚠️⚠️ The SUGGESTED objective is deliberately absent from this list — it is
  // the one placeholder a user can knowingly accept. See `domainMissions.ts`.
  if (MISSION_LENGTHS.some(len => text === getDefaultMission(domainId, len).trim())) {
    return 'untouched';
  }
  return 'authored';
}

/**
 * ⭐⭐ **THE ONE PREDICATE**, shared by Growth Journey step 3's completion, the
 * Build page's domain list, and (L5) which domains Generate Actions will act on.
 * Three surfaces that must agree about what "ready to operate through" means.
 *
 * ⚠️⚠️ **DERIVED, AND DELIBERATELY NOT A `RevealSurface`.** The reveal bank is
 * **append-only** by design so reveal can never regress; seven per-domain keys in
 * it would be permanent and un-removable, and un-writing a mission could never
 * take a domain back out. This reads live state, so it moves both ways.
 *
 * ⚠️ It is NOT a rung on `READINESS_LADDER` either — the ladder's rungs each
 * describe one fact about a domain, and this is a conjunction of two. Adding a
 * rung would also change `READINESS_META`, which several surfaces render.
 */
export function isMappedAndMissioned(
  domainId: string,
  readiness: Record<string, DomainReadiness>,
  missions: Record<string, string | undefined>,
): boolean {
  return buildBlock(domainId, readiness, missions) === null;
}

/**
 * ⭐⭐ **WHY A DOMAIN IS NOT ON THE BUILD PAGE — or `null` when it is.**
 *
 * ⚠️⚠️ **THE GATE IS DEFINED IN TERMS OF THIS, NOT ALONGSIDE IT.** A separate
 * "explain the absence" helper is exactly the kind of second copy that drifts:
 * it would keep saying "write an objective" for a domain the real rule had
 * started admitting, and nothing would fail. `isMappedAndMissioned` is now
 * `buildBlock(...) === null`, so an explanation that disagrees with the gate is
 * not expressible.
 */
/** ⚠️ The two mission states spell themselves the same as in `MissionState`, so
 *  `buildBlock` can return one straight through without a translation table. */
export type BuildBlock = 'unmapped' | 'missing' | 'untouched';

export function buildBlock(
  domainId: string,
  readiness: Record<string, DomainReadiness>,
  missions: Record<string, string | undefined>,
): BuildBlock | null {
  const rung = readiness[domainId] ?? 'unknown';
  if (readinessRank(rung) < readinessRank('mapped')) return 'unmapped';
  const state = missionState(missions, domainId);
  return state === 'authored' ? null : state;
}

/**
 * What to tell the user about each block, in their words rather than the rule's.
 * ⚠️ `fix` names the surface AND the tab — "write an objective" without saying
 * where is the complaint this whole change came from.
 */
export const BUILD_BLOCK_COPY: Record<BuildBlock, { reason: string; fix: string }> = {
  unmapped: {
    reason: 'not mapped yet',
    fix: 'Complete the Life Map — one sitting covers all seven domains.',
  },
  missing: {
    reason: 'no objective yet',
    fix: 'Open it in the Life Map and write one under Objective.',
  },
  untouched: {
    reason: 'its objective is still the starting text',
    fix: 'Open it in the Life Map, Objective, and make the wording your own.',
  },
};

/**
 * Which domains the Build page offers.
 *
 * ⚠️⚠️ **THE FAIL-OPEN RULES ARE THE POINT OF THIS FUNCTION**, and they are why
 * it is pure and tested rather than inline in the view:
 *
 *  · `filtering: false` → **every** domain, untouched. The caller passes false
 *    unless the account is reveal-ENROLLED **and** hydrated. `revealEnrolled` is
 *    `undefined` for every existing account, so this is inert for all of them;
 *    filtering unconditionally would hide domains people already build in, which
 *    is the 2026-08-03 lockout in a new costume.
 *  · The **selected** domain always survives. Un-writing a mission while looking
 *    at that domain would otherwise pull the card out from under the reader and
 *    strand the build area with no way back.
 */
export function domainsForBuild<T extends { id: string }>(
  domains: T[],
  opts: {
    filtering: boolean;
    selectedDomain: string | null;
    readiness: Record<string, DomainReadiness>;
    missions: Record<string, string | undefined>;
  },
): T[] {
  if (!opts.filtering) return domains;
  return domains.filter(d =>
    d.id === opts.selectedDomain || isMappedAndMissioned(d.id, opts.readiness, opts.missions));
}

export const READINESS_META: Record<DomainReadiness, { label: string; hint: string }> = {
  unknown:    { label: 'Not yet met',  hint: 'You have not looked at this part of your life yet' },
  discovered: { label: 'Discovered',   hint: 'You know what it covers' },
  mapped:     { label: 'Mapped',       hint: 'You know where you stand in it' },
  activated:  { label: 'Activated',    hint: 'It has a direction you are working toward' },
  developed:  { label: 'Developing',   hint: 'You have said you are actively working on it' },
};
